import { z } from 'zod/v4';
import {
  IHafasArrivalBoardResponse,
  IHafasColor,
  IHafasDepartureBoardResponse,
  IHafasEco,
  IHafasEcoComparison,
  IHafasReconTripResponse,
  IHafasIcon,
  IHafasJourney,
  IHafasJourneyDetailResponse,
  IHafasLocation,
  IHafasLocationNotes,
  IHafasMainMast,
  IHafasNotes,
  IHafasPlatform,
  IHafasProductAtStop,
  IHafasResponse,
  IHafasResult,
  IHafasServiceDay,
  IHafasStop,
  IHafasStopsResponse,
  IHafasTrip,
  IHafasTripLeg,
  IHafasTripResponse,
  IHafasProduct,
} from './hafas.interfaces';
import {
  JourneyLocationId,
  LatitudeCoordinate,
  LongitudeCoordinate,
} from './journey-planning-types.interfaces';

export const HAFAS_NOTES_SCHEMA: z.ZodType<IHafasNotes> = z.object({
  Note: z.array(
    z.object({
      value: z.string(),
      key: z.string(),
      type: z.string(),
      txtN: z.string(),
    })
  ),
});

export const HAFAS_LOCATION_NOTES_SCHEMA: z.ZodType<IHafasLocationNotes> =
  z.object({
    LocationNote: z.array(
      z.object({
        value: z.string(),
        key: z.string(),
        type: z.string(),
        txtN: z.string(),
      })
    ),
  });

export const HAFAS_PLATFORM_SCHEMA: z.ZodType<IHafasPlatform> = z.object({
  type: z.string(),
  text: z.string(),
});

export const HAFAS_COLOR: z.ZodType<IHafasColor> = z.object({
  r: z.string(),
  g: z.string(),
  b: z.string(),
  hex: z.string(),
});

export const HAFAS_ICON_SCHEMA: z.ZodType<IHafasIcon> = z.object({
  foregroundColor: HAFAS_COLOR,
  backgroundColor: HAFAS_COLOR,
  res: z.string(),
});

export const HAFAS_MAIN_MAST_SCHEMA: z.ZodType<IHafasMainMast> = z.object({
  LocationNotes: HAFAS_LOCATION_NOTES_SCHEMA,
  altId: z.array(z.string()),
  timezoneOffset: z.number(),
  id: z.string(),
  extId: z.string(),
  isMainMast: z.boolean(),
  name: z.string(),
  lon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  lat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  products: z.number(),
  meta: z.boolean(),
});

export const HAFAS_LOCATION_SCHEMA: z.ZodType<IHafasLocation> = z.object({
  Notes: HAFAS_NOTES_SCHEMA,
  altId: z.array(z.string()),
  mainMastAltId: z.array(z.string()),
  mainMast: HAFAS_MAIN_MAST_SCHEMA,
  platform: HAFAS_PLATFORM_SCHEMA,
  rtPlatform: HAFAS_PLATFORM_SCHEMA,
  name: z.string(),
  type: z.string(),
  id: z
    .string()
    .transform((s: string) => s as JourneyLocationId)
    .default('' as JourneyLocationId),
  extId: z.string(),
  lon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  lat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  routeIdx: z.number(),
  prognosisType: z.string(),
  time: z.string(),
  date: z.string(),
  track: z.string(),
  rtTime: z.string(),
  rtDate: z.string(),
  rtTrack: z.string(),
  hasMainMast: z.boolean(),
  mainMastId: z.string(),
  mainMastExtId: z.string(),
  mainMastLon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  mainMastLat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  entry: z.boolean(),
  minimumChangeDuration: z.string(),
});

export const HAFAS_ECO_SCHEMA: z.ZodType<IHafasEco> = z.object({
  co2: z.number(),
  dist: z.number(),
  type: z.string(),
  rating: z.number(),
  dur: z.string(),
});

export const HAFAS_ECO_COMPARISON_SCHEMA: z.ZodType<IHafasEcoComparison> =
  z.object({
    co2: z.number(),
    type: z.string(),
  });

export const HAFAS_SERVICE_DAY_SCHEMA: z.ZodType<IHafasServiceDay> = z.object({
  planningPeriodBegin: z.string(),
  planningPeriodEnd: z.string(),
  sDaysR: z.string(),
  sDaysI: z.string(),
  sDaysB: z.string(),
});

export const HAFAS_PRODUCT_SCHEMA: z.ZodType<IHafasProduct> = z.object({
  icon: z.array(HAFAS_ICON_SCHEMA),
  operatorInfo: z
    .object({
      name: z.string(),
      nameS: z.string(),
      nameN: z.string(),
      nameL: z.string(),
      id: z.string(),
      url: z.string().default(''),
    })
    .optional(),
  name: z.string(),
  internalName: z.string(),
  displayNumber: z.string(),
  num: z.string(),
  line: z.string(),
  lineId: z.string(),
  catIn: z.string(),
  catCode: z.string(),
  cls: z.string(),
  catOutS: z.string(),
  catOutL: z.string(),
  operatorCode: z.string(),
  operator: z.string(),
  admin: z.string(),
  routeIdxFrom: z.number(),
  routeIdxTo: z.number(),
  matchId: z.string(),
});

export const HAFAS_TRIP_LEG_SCHEMA: z.ZodType<IHafasTripLeg> = z.object({
  Origin: HAFAS_LOCATION_SCHEMA,
  Destination: HAFAS_LOCATION_SCHEMA,
  Eco: HAFAS_ECO_SCHEMA,
  Notes: HAFAS_NOTES_SCHEMA,
  Product: z.array(HAFAS_PRODUCT_SCHEMA),
  JourneyStatus: z.string(),
  id: z.string(),
  idx: z.number(),
  name: z.string(),
  number: z.string(),
  category: z.string(),
  type: z.string(),
  reachable: z.boolean(),
  waitingState: z.string(),
  direction: z.string(),
  directionFlag: z.string(),
  duration: z.string(),
  dist: z.number().optional(),
  minimumChangeDuration: z.string(),
  JourneyDetailRef: z
    .object({
      ref: z.string(),
    })
    .optional(),
});

export const HAFAS_TRIP_SCHEMA: z.ZodType<IHafasTrip> = z.object({
  Origin: HAFAS_LOCATION_SCHEMA,
  Destination: HAFAS_LOCATION_SCHEMA,
  Notes: HAFAS_NOTES_SCHEMA,
  Eco: HAFAS_ECO_SCHEMA,
  EcoCpm: z.array(HAFAS_ECO_COMPARISON_SCHEMA),
  ServiceDays: z.array(HAFAS_SERVICE_DAY_SCHEMA),
  LegList: z.object({
    Leg: z.array(HAFAS_TRIP_LEG_SCHEMA),
  }),
  TariffResult: z
    .object({
      Trip: z.object({
        Origin: HAFAS_LOCATION_SCHEMA,
        Destination: HAFAS_LOCATION_SCHEMA,
        Notes: HAFAS_NOTES_SCHEMA,
        Eco: HAFAS_ECO_SCHEMA,
        EcoCpm: z.array(HAFAS_ECO_COMPARISON_SCHEMA),
        ServiceDays: z.array(HAFAS_SERVICE_DAY_SCHEMA),
        LegList: z.object({
          Leg: z.array(HAFAS_TRIP_LEG_SCHEMA),
        }),
      }),
    })
    .nullable()
    .default(null),
  TripStatus: z.object({
    detour: z.boolean(),
    hint: z.number(),
  }),
  idx: z.number(),
  tripId: z.string(),
  calculation: z.string(),
  ctxRecon: z.string(),
  duration: z.string(),
  dist: z.number(),
  rtDuration: z.string(),
  checksum: z.string(),
  transferCount: z.number(),
});

export const HAFAS_PRODUCT_AT_STOP_SCHEMA: z.ZodType<IHafasProductAtStop> =
  z.object({
    icon: HAFAS_ICON_SCHEMA,
    name: z.string(),
    internalName: z.string(),
    line: z.string(),
    lineId: z.string(),
    catIn: z.string(),
    catCode: z.string(),
    cls: z.string(),
    catOutS: z.string(),
    catOutL: z.string(),
  });

export const HAFAS_RESULT_SCHEMA: z.ZodType<IHafasResult> = z.object({
  ResultStatus: z.object({
    timeDiffCritical: z.boolean(),
  }),
});

export const HAFAS_RESPONSE_SCHEMA: z.ZodType<IHafasResponse> = z.object({
  TechnicalMessages: z.object({
    TechnicalMessage: z.array(z.object({ key: z.string(), value: z.string() })),
  }),
  serverVersion: z.string(),
  dialectVersion: z.string(),
  requestId: z.string(),
});

export const HAFAS_STOP_SCHEMA: z.ZodType<IHafasStop> = z.object({
  LocationNotes: HAFAS_LOCATION_NOTES_SCHEMA,
  productAtStop: z.array(HAFAS_PRODUCT_AT_STOP_SCHEMA),
  timezoneOffset: z.number(),
  altId: z.array(z.string()),
  id: z.string(),
  extId: z.string(),
  name: z.string(),
  lon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  lat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  weight: z.number(),
  products: z.number(),
  meta: z.boolean(),
  minimumChangeDuration: z.string(),
});

export const HAFAS_JOURNEY_SCHEMA: z.ZodType<IHafasJourney> = z.object({
  JourneyDetailRef: z.object({
    ref: z.string(),
  }),
  JourneyStatus: z.string(),
  ProductAtStop: HAFAS_PRODUCT_AT_STOP_SCHEMA,
  Product: z.array(HAFAS_PRODUCT_SCHEMA),
  Notes: HAFAS_NOTES_SCHEMA,
  altId: z.array(z.string()),
  mainMastAltId: z.array(z.string()),
  platform: HAFAS_PLATFORM_SCHEMA,
  mainMast: HAFAS_MAIN_MAST_SCHEMA,
  name: z.string(),
  type: z.string(),
  stop: z.string(),
  stopid: z.string(),
  stopExtId: z.string(),
  lon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  lat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  hasMainMast: z.boolean(),
  mainMastId: z.string(),
  mainMastExtId: z.string(),
  mainMastLon: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  mainMastLat: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  time: z.string(),
  date: z.string(),
  track: z.string(),
  reachable: z.boolean(),
  direction: z.string(),
  directionFlag: z.string(),
  entry: z.boolean(),
});

export const HAFAS_STOPS_RESPONSE_SCHEMA: z.ZodType<IHafasStopsResponse> =
  z.intersection(
    z.object({
      stopLocationOrCoordLocation: z.array(
        z.object({
          StopLocation: HAFAS_STOP_SCHEMA,
        })
      ),
    }),
    HAFAS_RESPONSE_SCHEMA
  );

export const HAFAS_DEPARTURE_BOARD_RESPONSE_SCHEMA: z.ZodType<IHafasDepartureBoardResponse> =
  z.intersection(
    z.object({
      Departure: z.array(HAFAS_JOURNEY_SCHEMA),
      planRtTs: z.string(),
    }),
    HAFAS_RESPONSE_SCHEMA
  );

export const HAFAS_ARRIVAL_BOARD_RESPONSE_SCHEMA: z.ZodType<IHafasArrivalBoardResponse> =
  z.intersection(
    z.object({
      Arrival: z.array(HAFAS_JOURNEY_SCHEMA),
      planRtTs: z.string(),
    }),
    HAFAS_RESPONSE_SCHEMA
  );

export const HAFAS_RECON_TRIP_RESPONSE: z.ZodType<IHafasReconTripResponse> =
  z.intersection(
    z.object({
      Trip: z.array(HAFAS_TRIP_SCHEMA),
      planRtTs: z.string(),
      scrB: z.string(),
      scrF: z.string(),
    }),
    HAFAS_RESPONSE_SCHEMA
  );

export const HAFAS_JOURNEY_DETAIL_RESPONSE_SCHEMA: z.ZodType<IHafasJourneyDetailResponse> =
  z.object({
    Stops: z
      .object({
        Stop: z
          .array(
            z.object({
              altId: z.array(z.string()).optional(),
              mainMastAltId: z.array(z.string()).optional(),
              mainMast: z
                .object({
                  altId: z.array(z.string()).optional(),
                  name: z.string().optional(),
                })
                .optional(),
              id: z.string().optional(),
              extId: z.string().optional(),
              name: z.string().optional(),
              lon: z
                .number()
                .transform((n: number) => n as LongitudeCoordinate)
                .optional(),
              lat: z
                .number()
                .transform((n: number) => n as LatitudeCoordinate)
                .optional(),
              routeIdx: z.number().optional(),
            })
          )
          .optional(),
      })
      .optional(),
    errorCode: z.string().optional(),
    errorText: z.string().optional(),
  });

export const HAFAS_TRIP_RESPONSE_SCHEMA: z.ZodType<IHafasTripResponse> =
  z.intersection(HAFAS_RECON_TRIP_RESPONSE, HAFAS_RESULT_SCHEMA);
